import PageHero from "@/components/landing/PageHero";
import {
  Shield,
  Lock,
  Eye,
  Server,
  Users,
  Database,
  FileCheck,
  Globe,
  AlertTriangle,
  BrainCog,
  KeyRound,
  HardDrive,
  ClipboardCheck,
  PenLine,
  Boxes,
} from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

const sections = [
  {
    number: "1",
    title: "Infrastructure & Security",
    description:
      "Pharmalify Spine runs on a cloud-native foundation operated under controls aligned with internationally recognized security benchmarks.",
    icon: Server,
    items: [
      {
        title: "SOC 2-Aligned Controls",
        description:
          "Our hosting and operational controls are designed to align with SOC 2 Trust Services Criteria (Security, Availability, Confidentiality).\u00a0",
        icon: Shield,
      },
      {
        title: "Encryption at Rest & in Transit",
        description:
          "Customer Data is encrypted at rest and in transit (TLS) using the managed encryption provided by our cloud hosting infrastructure. Specific algorithms, key management and hosting details are documented in the security package shared during contracting.",
        icon: Lock,
      },
      {
        title: "High Availability & Resilience",
        description:
      "Managed cloud infrastructure, automated backups and recovery controls support the availability of Spine’s Quality (eQMS), QC Lab (LIMS) and Asset Integrity (CMMS) pillars, and the surrounding platform.",
        icon: Server,
      },
    ],
  },
  {
    number: "2",
    title: "Pharmalify Spine: Validation-Ready GxP Backbone",
    description:
      "Pharmalify Spine is engineered to support customer validation across quality, laboratory, maintenance, materials and asset records in one governed model.",
    icon: Boxes,
    items: [
      {
        title: "GAMP 5 / 21 CFR Part 11 Validation Package",
        description:
          "A validation package — including user requirements specifications (URS), risk assessment, a traceability matrix, IQ/OQ/PQ templates and execution support, and release notes — is available to support the Customer's own validation under GAMP 5 and 21 CFR Part 11 expectations.",
        icon: ClipboardCheck,
      },
      {
        title: "Part 11-Style E-Signatures & Workflow Sign-off",
        description:
          "Reviewer, approver and final sign-off steps capture identity, intent, timestamp and reason for action — designed to support 21 CFR Part 11 Subpart C requirements.",
        icon: PenLine,
      },
      {
        title: "Unified Quality, LIMS & CMMS Records",
        description:
          "Quality events, laboratory results, equipment, calibration, preventive maintenance and materials share a consistent audit, governance and access model.",
        icon: Boxes,
      },
    ],
  },
  {
    number: "3",
    title: "GxP Readiness & Data Integrity",
    description:
      'Pharmalify provides the native technical controls expected of a regulated computerized system, designed to align with FDA 21 CFR Part 11 and EU Annex 11:',
    icon: FileCheck,
    items: [
      {
        title: "Append-Only Audit Trail",
        description:
          'Every record creation, modification or soft-deletion is timestamped and attributed to a specific user, producing an append-only "Who, What, When, Why" history.',
        icon: Eye,
      },
      {
        title: "Role-Based Access Control (RBAC)",
        description:
          "Database-enforced access and segregation of duties (SoD) scope records, native AI Agents and workflows to each user’s authorized GxP function, site and organization.",
        icon: Users,
      },
      {
        title: "Data Integrity (ALCOA+)",
        description:
          "The data model is designed so records remain Attributable, Legible, Contemporaneous, Original, Accurate, Complete, Consistent, Enduring and Available.",
        icon: Database,
      },
    ],
  },
  {
    number: "4",
    title: "Privacy & Governance",
    description: "Customer Data is governed by strict privacy principles and regulatory-grade controls.",
    icon: Globe,
    items: [
      {
        title: "GDPR-Aligned",
        description:
          "Processing is designed around GDPR principles, including erasure requests subject to GxP retention obligations. Hosting region, data-residency needs and the Data Processing Addendum (DPA) are agreed per customer contract.",
        icon: Globe,
      },
      {
        title: "Contractual Privacy Controls",
        description:
          "Data-processing terms, retention requirements and regional deployment needs are agreed with each customer before regulated or sensitive data is introduced.",
        icon: Shield,
      },
      {
        title: "Tenant Isolation by Design",
        description:
          "Row-Level Security (RLS) and per-organization storage scoping enforce data isolation at the database and storage layer — not just in the UI.",
        icon: Lock,
      },
    ],
  },
  {
    number: "5",
    title: "AI Data Protection & Sovereignty",
    description: "Native AI Agents operate inside Spine’s compliance boundary as governed, audit-trailed GxP assistants. For document drafting, deviation triage, CAPA assistance and audit readiness, they propose and cite, never decide; authorized people retain approval authority.",
    icon: BrainCog,
    items: [
      {
        title: "No Model Training on Your Data",
        description:
          "Pharmalify does not use your inputs, outputs or uploaded documents to train or fine-tune AI models, and AI processing is routed through providers under terms that exclude training on submitted data. Provider details are listed in the contract documentation.",
        icon: BrainCog,
      },
      {
        title: "Full Data Sovereignty & Control",
        description:
          "You retain complete ownership and control over your data at all times. Data handling, retention and access are governed by agreed policies; regulated records remain subject to retention obligations and an append-only audit trail.",
        icon: KeyRound,
      },
      {
        title: "No Background or Cross-Tenant Training",
        description:
          "Customer records are not used for background model training, shared fine-tuning or cross-tenant retrieval. AI access remains scoped to the signed-in user's permissions.",
        icon: HardDrive,
      },
    ],
  },
];

const SecurityCompliance = () => {
  useSEO({
    title: "Pharmalify Spine | Biopharma Security & Validation Readiness",
    description:
      "Explore Pharmalify Spine’s biopharma audit readiness: 21 CFR Part 11, EU GMP Annex 11, GAMP 5, ALCOA+, validation support and governed native AI Agents.",
    keywords:
      "GAMP 5 validation, 21 CFR Part 11 software, ALCOA+ pharma, GDPR pharma SaaS, validation-ready eQMS, GxP cloud security",
    canonical: "https://www.pharmalify.com/security-compliance",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "TechArticle",
      headline: "Pharmalify Security, Validation & Compliance",
      description:
         "Technical controls supporting validation-ready Pharmalify Spine, biopharma audit readiness, native AI Agents and Regulatory Watch.",
      url: "https://www.pharmalify.com/security-compliance",
      author: { "@type": "Organization", name: "Pharmalify", url: "https://www.pharmalify.com" },
      publisher: { "@type": "Organization", name: "Pharmalify", url: "https://www.pharmalify.com" },
      about: [
        "SOC 2",
        "FDA 21 CFR Part 11",
        "EU Annex 11",
        "GAMP 5",
        "ALCOA+",
        "GDPR",
        "HIPAA",
      ],
    },
  });
  return (
    <main className="min-h-screen bg-background">
      <PageHero
        title="Trust, Validation and Data Integrity"
         description="Security, data integrity and biopharma audit readiness for Pharmalify Spine: Quality (eQMS), QC Lab (LIMS), Asset Integrity (CMMS), and native AI Agents and Regulatory Watch."
      />

      <section className="py-16 md:py-24">
        <div className="container max-w-5xl">
          {/* Compliance posture summary */}
          <div className="mb-14 p-6 md:p-8 rounded-2xl border border-border bg-card">
            <h2 className="text-xl font-semibold text-foreground mb-3">
              Our compliance posture, in plain terms
            </h2>
            <p className="text-muted-foreground leading-relaxed">
                Pharmalify Spine is <strong>designed to support regulated use</strong> through Part 11-style electronic signatures, append-only audit trails, database-enforced access controls and validation evidence aligned with 21 CFR Part 11, EU GMP Annex 11, GAMP 5 and ALCOA+ principles for biopharma audit readiness. Formal validation is completed for each customer's intended use.{"\u00a0"}
            </p>
          </div>

          {sections.map((section) => (
            <div key={section.number} className="mb-16 last:mb-0">
              {/* Section header */}
              <div className="flex items-start gap-4 mb-8">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-accent/15 flex items-center justify-center text-accent font-bold text-lg">
                  {section.number}
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                    {section.title}
                  </h2>
                  {section.description && (
                    <p className="text-muted-foreground leading-relaxed max-w-3xl">
                      {section.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Items grid */}
              <div className="grid md:grid-cols-3 gap-6 ml-0 md:ml-14">
                {section.items.map((item) => (
                  <div key={item.title} className="pillar-card">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                      <item.icon className="w-5 h-5 text-accent" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Compliance Note */}
          <div className="mt-16 p-6 rounded-2xl border border-accent/20 bg-accent/5">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-accent/15 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">Shared-Responsibility Note</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Pharmalify provides the technical features, security controls and a validation package designed to support a compliant environment. The final validation of Pharmalify Spine for any specific GxP use — including user-requirement specifications, risk assessments, IQ/OQ/PQ execution, periodic review and change control under the Customer's QMS — remains the responsibility of the regulated entity (the Customer) based on its intended use and internal procedures.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SecurityCompliance;
